import { Link, useParams, Navigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PUBLIC_SAMPLE_GRANTS, formatMoney, formatDeadline } from "@/lib/mockData";
import MatchRing from "@/components/common/MatchRing";
import SiteNav from "@/components/layout/SiteNav";
import SiteFooter from "@/components/layout/SiteFooter";
import { ArrowRight, Building2, Calendar, ShieldCheck, CheckCircle2, Eye } from "lucide-react";

/**
 * Public, read-only showcase pages. NEVER reads user data and NEVER persists
 * anything. Safe to share the URL with anyone on any device.
 */

export default function Demo() {
    // Public, static sample — NEVER personalized, safe to share
    const sample = PUBLIC_SAMPLE_GRANTS;
    return (
        <div className="min-h-screen bg-background text-foreground">
            <SiteNav />
            <div className="mx-auto max-w-6xl px-5 sm:px-8 py-14">
                <div className="inline-flex items-center gap-2 rounded-full bg-secondary border border-border px-3 py-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    <Eye className="h-3.5 w-3.5" /> Public demo · no account required
                </div>
                <h1 className="mt-5 font-serif text-5xl sm:text-6xl leading-tight tracking-tight">What a real match report looks like.</h1>
                <p className="mt-4 max-w-2xl text-lg text-muted-foreground leading-relaxed">
                    This is a sample only. When you sign up, your results are personalized to your profile — and nobody but you can see them.
                </p>
                <div className="mt-8 grid md:grid-cols-3 gap-5">
                    {sample.map((g) => (
                        <Link to={`/demo/grant/${g.id}`} key={g.id} className="group">
                            <Card className="p-6 h-full border-border shadow-soft hover:shadow-elegant transition-shadow">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{g.orgShort}</p>
                                        <h3 className="mt-2 font-serif text-xl leading-tight line-clamp-2">{g.title}</h3>
                                    </div>
                                    <MatchRing score={g.matchScore} size={44} />
                                </div>
                                <p className="mt-3 text-sm text-muted-foreground line-clamp-2 leading-relaxed">{g.description}</p>
                                <div className="mt-5 flex items-end justify-between pt-4 border-t border-border">
                                    <p className="font-serif text-2xl">{formatMoney(g.amount, { compact: true })}</p>
                                    <span className="text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">Preview <ArrowRight className="h-4 w-4" /></span>
                                </div>
                            </Card>
                        </Link>
                    ))}
                </div>
                <div className="mt-10 flex flex-wrap gap-3">
                    <Button asChild size="lg" className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-12 px-7">
                        <Link to="/onboarding">Run my free scan <ArrowRight className="h-4 w-4 ml-1" /></Link>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7">
                        <Link to="/">Back to home</Link>
                    </Button>
                </div>
            </div>
            <SiteFooter />
        </div>
    );
}

export function DemoProgram() {
    const { id } = useParams();
    const g = PUBLIC_SAMPLE_GRANTS.find((x) => x.id === id);
    if (!g) return <Navigate to="/demo" replace />;
    return (
        <div className="min-h-screen bg-background text-foreground">
            <SiteNav />
            <div className="mx-auto max-w-5xl px-5 sm:px-8 py-12">
                <div className="inline-flex items-center gap-2 rounded-full bg-secondary border border-border px-3 py-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    <Eye className="h-3.5 w-3.5" /> Public preview · no account data shown
                </div>
                <Card className="mt-6 p-8 sm:p-10 border-border">
                    <div className="flex items-start gap-2 flex-wrap">
                        <Badge variant="outline" className="bg-secondary/60 border-border capitalize">{g.category}</Badge>
                        {g.difficulty && <Badge variant="outline" className="bg-secondary/60 border-border">{g.difficulty}</Badge>}
                    </div>
                    <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight">{g.title}</h1>
                    <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">{g.description}</p>
                    <div className="mt-6 flex flex-wrap gap-6">
                        {g.org && (
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Issuer</p>
                                <p className="mt-1 text-sm font-medium flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {g.org}</p>
                            </div>
                        )}
                        {g.deadline && (
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Deadline</p>
                                <p className="mt-1 text-sm font-medium flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {formatDeadline(g.deadline)}</p>
                            </div>
                        )}
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Estimated value</p>
                            <p className="mt-1 font-serif text-2xl">{formatMoney(g.amount, { compact: g.amount >= 100000 })}</p>
                        </div>
                    </div>
                </Card>
                {g.eligibility && (
                    <Card className="mt-5 p-6 border-border">
                        <h2 className="font-serif text-2xl">Eligibility</h2>
                        <ul className="mt-4 space-y-2">
                            {g.eligibility.map((e, i) => (
                                <li key={i} className="flex items-start gap-3 text-sm">
                                    <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" /> <span>{e}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                )}
                <Card className="mt-5 p-6 bg-gradient-warm border-0">
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <ShieldCheck className="h-4 w-4 text-success" />
                        <p className="text-xs font-mono uppercase tracking-wider">Next step</p>
                    </div>
                    <h3 className="mt-2 font-serif text-2xl leading-tight">Want this matched to YOUR profile — with the packet auto-drafted?</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">Owed tailors match scores to your situation, pre-fills every form, and walks you through e-sign + filing. Your data stays private — always.</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                        <Button asChild className="rounded-full bg-foreground text-background hover:bg-foreground/90"><Link to="/onboarding">Start my free scan</Link></Button>
                        <Button asChild variant="outline" className="rounded-full"><Link to="/demo">See other samples</Link></Button>
                    </div>
                </Card>
            </div>
            <SiteFooter />
        </div>
    );
}
